import { detailsBase } from './billing/serviceDetailsConcurrency';
import type { TFunction } from 'i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentIcon from '@mui/icons-material/Payment';
import PreviewIcon from '@mui/icons-material/Preview';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SendIcon from '@mui/icons-material/Send';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { GroupedInvoiceLines } from './GroupedInvoiceLines';
import { InvoiceSummaryCard } from './InvoiceSummaryCard';
import { PreviousInvoicesSection } from './PreviousInvoicesSection';
import { GenerateInvoiceDialog, type GeneratedInvoiceDocuments } from './GenerateInvoiceDialog';
import { SveaPaymentSection } from '@/features/invoices/SveaPaymentSection';
import { PostInvoiceDocumentsDialog } from './PostInvoiceDocumentsDialog';
import { PreInvoiceChecklistDialog } from './PreInvoiceChecklistDialog';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import {
  openInvoicePdf,
  openInvoicePreviewPdf,
  printInvoicePdf,
  printReceiptPdf,
  useDeliverInvoice,
  useInvoiceDetail,
  useInvoicePreview,
  useVoidInvoice,
  useWorkOrderInvoicesHistory,
  type InvoiceDetail,
  type WorkOrderInvoice,
} from '@/hooks/useInvoices';
import { useAuthStore } from '@/stores/authStore';
import { INVOICE_READ_PERMISSIONS } from '@/lib/invoicePermissions';
import { PaymentHistorySection, PaymentLedgerProvider } from './PaymentHistorySection';
import { useBillActualHours, type WorkOrderDetail } from '@/hooks/useWorkOrders';
import { useUpdateBooking } from '@/hooks/useBookings';
import { useNotify } from '@/hooks/useNotify';
import { useWorkOrderBillingLines } from '@/hooks/useWorkOrderBillingLines';
import { translateBillingError } from '@/lib/billingErrors';
import { describeApiError } from '@/lib/describeApiError';
import { EDIT_BILLABLE_HOURS_PERMISSION } from '@/lib/billableHours';
import { closedActualHours, formatDecimalHoursLabel } from '@/utils/actualTime';
import { eurosToCents, grossToNetCents } from '@/utils/money';
import { BillActualServices } from './BillActualServices';
import { WorkOrderSurchargeRow } from './WorkOrderSurchargeRow';
import { DEFAULT_VAT_PERCENT, netToGrossCents } from '@/utils/vat';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { CreditNoteDialog } from '@/features/invoices/CreditNoteDialog';
import {
  annotateBillingLines,
  findServiceByBookingId,
  findServiceByName,
  payloadAfterAddExtra,
  payloadAfterExtraPatch,
  payloadAfterFixedPriceOverride,
  payloadAfterLaborPatch,
  payloadAfterPartPatch,
  workOrderItemCostFromUnitNet,
  type AnnotatedBillingLine,
} from './billing/billingLineSource';
import { BillingLineActionsContext } from './billing/billingLineActions';
import { isFixedPricePackage } from './fixedPricePackage';

const LOCKED_STATUSES: readonly string[] = ['invoiced', 'completed', 'cancelled'];
const VOID_BLOCKED_STATUSES = new Set(['void', 'refunded', 'credited', 'paid', 'partial']);

interface WorkOrderPaymentsProps {
  workOrder: WorkOrderDetail;
  workOrderInvoice?: WorkOrderInvoice;
  cardVariant: 'elevation' | 'outlined';
  t: TFunction;
  formatPrice: (cents: number) => string;
  /** Open a service's steps in the Works card — where combined labour is edited (#2638). */
  onOpenServiceLabor?: ((bookingServiceId: string) => void) | undefined;
}

interface OverflowAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  to?: string;
}

function billingErrorCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null || !('data' in err)) return undefined;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('code' in data)) return undefined;
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function isInvoiceVoidable(invoice: WorkOrderInvoice, detail: InvoiceDetail | undefined): boolean {
  if (VOID_BLOCKED_STATUSES.has(invoice.status) || invoice.paidAmountCents > 0) return false;
  if (detail?.deliveries.some((delivery) => delivery.status === 'sent')) return false;
  return true;
}

function isInvoiceCreditable(invoice: WorkOrderInvoice, detail: InvoiceDetail | undefined): boolean {
  const invoiceType = detail?.invoiceType ?? 'invoice';
  if (invoiceType !== 'invoice') return false;
  return invoice.status !== 'draft' && invoice.status !== 'void' && invoice.status !== 'credited';
}

/** Pause after the last keystroke before PATCHing the hourly rate (#2654). */
const LABOR_RATE_SAVE_DEBOUNCE_MS = 400;

function parseHourlyRateDraft(draft: string): number | null {
  const trimmed = draft.trim();
  if (trimmed === '' || /[.,]$/.test(trimmed)) return null;
  return eurosToCents(trimmed.replace(',', '.'));
}

function formatHourlyRateCents(cents: number): string {
  return cents > 0 ? (cents / 100).toFixed(2) : '';
}

export function WorkOrderPayments({
  workOrder,
  workOrderInvoice,
  cardVariant,
  t,
  formatPrice,
  onOpenServiceLabor,
}: WorkOrderPaymentsProps) {
  const { t: ti } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const updateBooking = useUpdateBooking();
  const notify = useNotify();
  const voidInvoice = useVoidInvoice();
  const deliverInvoice = useDeliverInvoice();
  const [generateOpen, setGenerateOpen] = useState(false);
  const [postInvoiceDocuments, setPostInvoiceDocuments] =
    useState<GeneratedInvoiceDocuments | null>(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
  const [voidErrorOfferCredit, setVoidErrorOfferCredit] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const billing = useWorkOrderBillingLines(workOrder.id);
  const displayRateCents = workOrder.laborRateGrossCents != null && workOrder.laborRateGrossCents > 0
    ? workOrder.laborRateGrossCents
    : workOrder.laborRateCents != null && workOrder.laborRateCents > 0
      ? netToGrossCents(workOrder.laborRateCents, workOrder.vatPercent ?? DEFAULT_VAT_PERCENT)
      : 0;
  const [rateDraft, setRateDraft] = useState(() => formatHourlyRateCents(displayRateCents));
  const rateDraftRef = useRef(rateDraft);
  rateDraftRef.current = rateDraft;
  const dirtyRateRef = useRef(false);
  const laborRateSaveEpochRef = useRef(0);
  const laborRateInFlightRef = useRef(false);
  const laborRateQueuedCentsRef = useRef<number | null>(null);
  const lastSubmittedRateCentsRef = useRef<number | null>(null);
  const lastFailedRateCentsRef = useRef<number | null>(null);
  const persistHourlyRateRef = useRef<(reason: 'blur' | 'enter' | 'debounce') => void>(() => {});
  const flushHourlyRateSaveRef = useRef<() => Promise<void>>(async () => {});
  const [rateUpdating, setRateUpdating] = useState(false);
  useEffect(() => {
    if (dirtyRateRef.current) return;
    setRateDraft(formatHourlyRateCents(displayRateCents));
  }, [displayRateCents]);
  const [linePendingDelete, setLinePendingDelete] = useState<AnnotatedBillingLine | null>(null);
  // #1315: the history route is permission-guarded, so don't fire it for roles
  // that would only get a 403 back.
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canEditBillableHours = hasPermission(EDIT_BILLABLE_HOURS_PERMISSION);
  const billActual = useBillActualHours();
  const [billActualServiceId, setBillActualServiceId] = useState<string | null>(null);
  const canReadInvoices = hasAnyPermission(INVOICE_READ_PERMISSIONS);
  const invoicesHistory = useWorkOrderInvoicesHistory(
    workOrder.id,
    canReadInvoices,
  );
  const invoiceDetail = useInvoiceDetail(workOrderInvoice?.id);
  const preview = useInvoicePreview(
    workOrder.id,
    workOrder.vatPercent ?? 25.5,
    0,
    !workOrderInvoice,
  );
  const invoiceLineItems = invoiceDetail.data?.lineItems;
  const previewLineItems = preview.data?.lineItems;
  const billingTotals = invoiceDetail.data ?? preview.data;
  // Verollinen loppusumma tulee laskulta tai esikatselusta. Jos kumpaakaan ei
  // ole, summaa EI arvioida totalPartsCents + totalLaborCents -summasta: ne ovat
  // nettoa, ja tämän rivin otsikko lupaa verollista (docs/VAT-BASIS.md § 5).
  const displayTotalCents = billingTotals?.totalCents ?? workOrder.totalWithVatCents ?? null;
  const canManageInvoices = hasAnyPermission(['invoices.manage']);
  const canTakePayments = hasAnyPermission(['payments.take', 'invoices.manage']);
  const remainingCents = workOrderInvoice
    ? workOrderInvoice.totalCents - workOrderInvoice.paidAmountCents
    : 0;
  const invoiceSettled = !!workOrderInvoice && (
    workOrderInvoice.status === 'paid'
    || remainingCents <= 0
  );
  const billingLocked =
    LOCKED_STATUSES.includes(workOrder.status)
    || workOrderInvoice !== undefined
    || !canManageInvoices;
  const editable = !billingLocked && editMode;
  useEffect(() => {
    setEditMode(false);
    dirtyRateRef.current = false;
    lastSubmittedRateCentsRef.current = null;
    lastFailedRateCentsRef.current = null;
    laborRateQueuedCentsRef.current = null;
    laborRateSaveEpochRef.current += 1;
    setRateUpdating(false);
    return () => {
      laborRateQueuedCentsRef.current = null;
      laborRateSaveEpochRef.current += 1;
    };
  }, [workOrder.id]);
  useEffect(() => {
    if (billingLocked) setEditMode(false);
  }, [billingLocked]);
  const fallbackTaxPercent = invoiceDetail.data?.taxPercent ?? workOrder.vatPercent ?? 25.5;
  const annotatedLines = useMemo(
    () => {
      const lines = annotateBillingLines(invoiceLineItems ?? previewLineItems ?? [], {
        ...workOrder,
        consumedParts: billing.consumedParts ?? [],
      }, {
        includeUnmatchedItems: !billingLocked,
        fallbackTaxPercent,
      });
      if (canEditBillableHours) return lines;
      return lines.map((line) => {
        if (line.source?.kind !== 'labor') return line;
        return {
          ...line,
          source: {
            ...line.source,
            capabilities: {
              ...line.source.capabilities,
              quantity: false,
              deletable: false,
            },
          },
        };
      });
    },
    [invoiceLineItems, previewLineItems, workOrder, billingLocked, fallbackTaxPercent, billing.consumedParts, canEditBillableHours],
  );
  const hoursBillingLocked =
    LOCKED_STATUSES.includes(workOrder.status)
    || workOrderInvoice !== undefined;
  const billActualHoursLabel = formatDecimalHoursLabel(
    closedActualHours(
      workOrder.services.find((service) => service.bookingServiceId === billActualServiceId),
    ),
  );
  const billingBlocked = workOrder.isDisputed || workOrder.isInvoicingHold;
  const primaryAction = ((): { kind: 'generate' | 'pay'; label: string; disabled?: boolean } | null => {
    if (billingBlocked) {
      if (!canManageInvoices) return null;
      return { kind: 'generate', label: t('serviceActions.paymentEntry'), disabled: true };
    }
    if (workOrderInvoice && !invoiceSettled) {
      if (!canTakePayments) return null;
      return { kind: 'pay', label: t('serviceActions.recordPayment') };
    }
    if (workOrder.status === 'ready_to_invoice' && !workOrderInvoice) {
      if (!canManageInvoices) return null;
      return { kind: 'generate', label: t('serviceActions.paymentEntry') };
    }
    return null;
  })();

  const canVoidInvoice = workOrderInvoice
    ? isInvoiceVoidable(workOrderInvoice, invoiceDetail.data)
    : false;
  const canCreditInvoice = workOrderInvoice
    ? isInvoiceCreditable(workOrderInvoice, invoiceDetail.data)
    : false;
  const latestReceiptId = (invoiceDetail.data?.payments ?? workOrderInvoice?.payments ?? [])
    .map((payment) => payment.receiptId)
    .find((id): id is string => Boolean(id));
  // Receipt / invoice recipient suggestion. A separate billing party wins with
  // its invoice email, then its contact email. When the orderer is billed, the
  // work order's own contact email (the inline edit on the customer card,
  // which does not touch the person record) counts before the person's
  // email — staff typing it there expect the receipt to go to it (#3113).
  const separateBillingParty = workOrder.parties?.find((party) => party.role === 'billing_party');
  const ordererParty = workOrder.parties?.find((party) => party.role === 'orderer');
  const customerEmail = separateBillingParty
    ? separateBillingParty.invoiceEmail?.trim() || separateBillingParty.email
    : ordererParty?.invoiceEmail?.trim()
      || workOrder.contactEmail?.trim()
      || ordererParty?.email
      || workOrder.customer?.email
      || null;

  // After a mistaken card payment is voided the invoice is open again (#3115).
  // Offer the two real continuations side by side instead of hiding "send as
  // invoice" in the overflow menu as "resend".
  const hasReversedPayment = (invoiceDetail.data?.payments ?? workOrderInvoice?.payments ?? [])
    .some((payment) => payment.paymentMethod === 'reversal');
  const canSendAsInvoice = Boolean(
    workOrderInvoice
      && canManageInvoices
      && !invoiceSettled
      && (workOrderInvoice.status === 'sent' || workOrderInvoice.status === 'partial'),
  );
  const handleSendAsInvoice = () => {
    if (!workOrderInvoice) return;
    void deliverInvoice.mutateAsync({
      invoiceId: workOrderInvoice.id,
      workOrderId: workOrder.id,
      ...(customerEmail ? { recipientEmail: customerEmail } : {}),
    }).then(() => {
      notify.success(ti('actions.deliverSuccess'));
    }).catch((err: unknown) => {
      notify.error(tc('error'), translateBillingError(err, ti));
    });
  };

  const overflowActions = useMemo<OverflowAction[]>(() => {
    if (!workOrderInvoice) return [];
    const actions: OverflowAction[] = [
      {
        key: 'print',
        label: ti('actions.print'),
        icon: <PrintIcon fontSize="small" />,
        onClick: () => {
          void printInvoicePdf(workOrderInvoice.id).then((result) => {
            if (result.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
          }).catch((err: unknown) => {
            notify.error(tc('error'), translateBillingError(err, ti));
          });
        },
      },
    ];
    if (latestReceiptId) {
      actions.push({
        key: 'print-receipt',
        label: ti('actions.printReceipt'),
        icon: <ReceiptLongIcon fontSize="small" />,
        onClick: () => {
          void printReceiptPdf(latestReceiptId).then((result) => {
            if (result.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
          }).catch((err: unknown) => {
            notify.error(tc('error'), translateBillingError(err, ti));
          });
        },
      });
    }
    if (canManageInvoices && workOrderInvoice.status !== 'void' && workOrderInvoice.status !== 'draft') {
      actions.push({
        key: 'resend',
        label: ti('actions.resend'),
        icon: <SendIcon fontSize="small" />,
        onClick: () => {
          void deliverInvoice.mutateAsync({
            invoiceId: workOrderInvoice.id,
            workOrderId: workOrder.id,
            ...(customerEmail ? { recipientEmail: customerEmail } : {}),
          }).then(() => {
            notify.success(ti('actions.deliverSuccess'));
          }).catch((err: unknown) => {
            notify.error(tc('error'), translateBillingError(err, ti));
          });
        },
      });
    }
    if (canManageInvoices && canVoidInvoice) {
      actions.push({
        key: 'void',
        label: ti('actions.void'),
        icon: <BlockIcon fontSize="small" />,
        onClick: () => {
          setVoidErrorOfferCredit(null);
          setVoidOpen(true);
        },
      });
    }
    if (canManageInvoices && canCreditInvoice) {
      actions.push({
        key: 'credit',
        label: ti('actions.creditNote'),
        icon: <ReceiptLongIcon fontSize="small" />,
        onClick: () => setCreditOpen(true),
      });
    }
    actions.push({
      key: 'open',
      label: ti('actions.openInvoice'),
      icon: <OpenInNewIcon fontSize="small" />,
      to: `/invoices/${workOrderInvoice.id}`,
    });
    return actions;
  }, [
    canCreditInvoice,
    canManageInvoices,
    canVoidInvoice,
    customerEmail,
    deliverInvoice,
    latestReceiptId,
    notify,
    tc,
    ti,
    workOrder.id,
    workOrderInvoice,
  ]);

  const handlePrimaryAction = () => {
    if (!primaryAction || primaryAction.disabled) return;
    if (primaryAction.kind === 'pay') {
      setPaymentOpen(true);
      return;
    }
    setChecklistOpen(true);
  };

  const handleChecklistConfirm = () => {
    setChecklistOpen(false);
    setGenerateOpen(true);
  };

  const laborCount = workOrder.services.reduce(
    (sum, service) => sum + service.laborItems.length,
    0,
  );
  const partsCount = workOrder.services.reduce(
    (sum, service) => sum + service.parts.length,
    0,
  ) + billing.consumedParts.length;

  const handleVoid = async () => {
    if (!workOrderInvoice) return;
    try {
      await voidInvoice.mutateAsync({
        invoiceId: workOrderInvoice.id,
        reason: ti('actions.voidDefaultReason'),
        workOrderId: workOrder.id,
      });
      notify.success(ti('actions.voidSuccess'));
      setVoidOpen(false);
      setVoidErrorOfferCredit(null);
    } catch (err) {
      const code = billingErrorCode(err);
      const message = code === 'invoice_already_delivered'
        ? ti('error.invoiceAlreadyDelivered')
        : translateBillingError(err, ti);
      notify.error(tc('error'), message);
      if (code === 'invoice_already_delivered' || code === 'invalid_invoice_state') {
        setVoidErrorOfferCredit(message);
      }
    }
  };

  const historyWithPayments = (invoicesHistory.data ?? []).filter((inv) => inv.paidAmountCents > 0);
  const historyInvoiceId = !workOrderInvoice && historyWithPayments[0]
    ? historyWithPayments[0].id
    : undefined;
  const historyDetail = useInvoiceDetail(historyInvoiceId);
  const paymentLedgerContext = {
    workOrderId: workOrder.id,
    ...(workOrderInvoice
      ? { invoiceId: workOrderInvoice.id }
      : historyInvoiceId
        ? { invoiceId: historyInvoiceId }
        : {}),
  };

  const mutationHandlers = {
    onSuccess: () => notify.success(ti('workOrderBilling.lineUpdated')),
    onError: () => notify.error(ti('workOrderBilling.lineUpdateFailed')),
  };

  const handleCommitQuantity = (line: AnnotatedBillingLine, quantity: number) => {
    const source = line.source;
    if (!source) return;
    if (source.kind === 'labor') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterLaborPatch(service, source.laborItemId, { estimatedHours: quantity }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      const nextQuantity = quantity < 0.01 ? 0.01 : quantity;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, { quantity: nextQuantity }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      // updatePartSchema.quantity is z.number().int().min(1); a decimal or 0 is 400.
      const nextQuantity = Math.max(1, Math.round(quantity));
      billing.updateConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
        data: { quantity: nextQuantity },
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'extra') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterExtraPatch(service, source.extraId, { quantity }),
      }, mutationHandlers);
    }
  };

  const handleCommitUnitPrice = (line: AnnotatedBillingLine, unitPriceGrossCents: number) => {
    const source = line.source;
    if (!source) return;
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, { unitPriceGrossCents }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'extra') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterExtraPatch(service, source.extraId, { amountGrossCents: unitPriceGrossCents }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'workOrderItem') {
      const vat = line.taxPercent ?? workOrder.vatPercent ?? DEFAULT_VAT_PERCENT;
      const unitNetCents = grossToNetCents(unitPriceGrossCents, line.taxable ? vat : 0);
      const netCents = workOrderItemCostFromUnitNet(line, source.costField, unitNetCents);
      billing.updateManualLine.mutate({
        workOrderId: workOrder.id,
        itemId: source.itemId,
        data: source.costField === 'partsCostCents'
          ? { partsCostCents: netCents }
          : { laborCostCents: netCents },
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      const vat = line.taxPercent ?? workOrder.vatPercent ?? DEFAULT_VAT_PERCENT;
      const netCents = grossToNetCents(unitPriceGrossCents, line.taxable ? vat : 0);
      billing.updateConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
        data: { unitSellPriceCents: netCents },
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'fixedService') {
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterFixedPriceOverride(unitPriceGrossCents),
      }, mutationHandlers);
    }
  };

  const handleResetFixedServicePrice = (line: AnnotatedBillingLine) => {
    const source = line.source;
    if (source?.kind !== 'fixedService') return;
    billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
      workOrderId: workOrder.id,
      bookingServiceId: source.bookingServiceId,
      data: payloadAfterFixedPriceOverride(null),
    }, mutationHandlers);
  };

  const handleCommitDiscount = (line: AnnotatedBillingLine, discountPercent: number) => {
    const source = line.source;
    if (!source) return;
    if (source.kind === 'labor') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterLaborPatch(service, source.laborItemId, { discountPercent }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, { discountPercent }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      billing.updateConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
        data: { discountPercent },
      }, mutationHandlers);
    }
  };

  const handleConfirmDelete = () => {
    const line = linePendingDelete;
    setLinePendingDelete(null);
    if (!line?.source) return;
    const source = line.source;
    const deleteHandlers = {
      onSuccess: () => notify.success(ti('workOrderBilling.lineDeleted')),
      onError: () => notify.error(ti('workOrderBilling.lineDeleteFailed')),
    };
    if (source.kind === 'labor') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterLaborPatch(service, source.laborItemId, {}, true),
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, {}, true),
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      billing.deleteConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'extra') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterExtraPatch(service, source.extraId, {}, true),
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'workOrderItem') {
      billing.deleteManualLine.mutate({
        workOrderId: workOrder.id,
        itemId: source.itemId,
      }, deleteHandlers);
    }
  };

  const handleConfirmBillActual = async () => {
    if (billActualServiceId == null) return;
    try {
      await billActual.mutateAsync({
        workOrderId: workOrder.id,
        bookingServiceId: billActualServiceId,
      });
      notify.success(t('detail.billActualSuccess'));
      setBillActualServiceId(null);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('detail.billActualFailed') }));
    }
  };

  const handleAddLine = (groupName: string | null, groupSortOrder?: number) => {
    const addHandlers = {
      onSuccess: () => notify.success(ti('workOrderBilling.lineAdded')),
      onError: () => notify.error(ti('workOrderBilling.lineAddFailed')),
    };
    if (groupName) {
      const service = findServiceByName(workOrder, groupName, groupSortOrder);
      // A fixed-price package bills one line; extra work goes outside it (#3655).
      if (service && !isFixedPricePackage(service)) {
        billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, service.bookingServiceId),
          workOrderId: workOrder.id,
          bookingServiceId: service.bookingServiceId,
          data: payloadAfterAddExtra(service, {
            description: ti('workOrderBilling.newLineDescription'),
            amountGrossCents: 0,
            isTaxable: true,
            hasVariableQuantity: true,
            unit: 'piece',
            quantity: 1,
            sortOrder: service.extras.length,
          }),
        }, addHandlers);
        return;
      }
    }
    billing.addManualLine.mutate({
      workOrderId: workOrder.id,
      data: {
        description: ti('workOrderBilling.newLineDescription'),
        category: 'manual',
        partsCostCents: 0,
        laborCostCents: 0,
      },
    }, addHandlers);
  };

  // One in-flight PATCH at a time; the queue slot holds only the latest cents (#2654).
  flushHourlyRateSaveRef.current = async () => {
    if (laborRateInFlightRef.current) return;
    laborRateInFlightRef.current = true;
    const runnerEpoch = laborRateSaveEpochRef.current;
    try {
      while (laborRateQueuedCentsRef.current != null) {
        if (runnerEpoch !== laborRateSaveEpochRef.current) break;
        const cents = laborRateQueuedCentsRef.current;
        if (
          cents === lastSubmittedRateCentsRef.current
          && lastFailedRateCentsRef.current !== cents
        ) {
          laborRateQueuedCentsRef.current = null;
          break;
        }
        const workOrderId = workOrder.id;
        laborRateQueuedCentsRef.current = null;
        lastFailedRateCentsRef.current = null;
        lastSubmittedRateCentsRef.current = cents;
        setRateUpdating(true);
        try {
          await updateBooking.mutateAsync({
            id: workOrderId,
            data: { laborRateGrossCents: cents },
          });
        } catch {
          if (runnerEpoch !== laborRateSaveEpochRef.current) break;
          if (
            laborRateQueuedCentsRef.current != null
            && laborRateQueuedCentsRef.current !== cents
          ) {
            continue;
          }
          lastFailedRateCentsRef.current = cents;
          notify.error(t('detail.laborRateUpdateFailed'));
          setRateUpdating(false);
          break;
        }
        if (runnerEpoch !== laborRateSaveEpochRef.current) break;
        if (laborRateQueuedCentsRef.current != null) continue;
        const latestDraftCents = parseHourlyRateDraft(rateDraftRef.current);
        if (latestDraftCents != null && latestDraftCents !== cents) {
          dirtyRateRef.current = true;
          break;
        }
        if (latestDraftCents == null && dirtyRateRef.current) {
          break;
        }
        dirtyRateRef.current = false;
        notify.success(t('detail.laborRateUpdated'));
        const refetchEpoch = laborRateSaveEpochRef.current;
        void Promise.resolve(preview.refetch()).finally(() => {
          if (refetchEpoch !== laborRateSaveEpochRef.current) return;
          if (laborRateInFlightRef.current || laborRateQueuedCentsRef.current != null) return;
          setRateUpdating(false);
        });
        break;
      }
    } finally {
      laborRateInFlightRef.current = false;
      if (laborRateQueuedCentsRef.current != null) {
        void flushHourlyRateSaveRef.current();
      }
    }
  };

  persistHourlyRateRef.current = (reason) => {
    const cents = parseHourlyRateDraft(rateDraftRef.current);
    if (cents == null) {
      if (reason === 'blur') {
        dirtyRateRef.current = false;
        setRateUpdating(false);
        setRateDraft(formatHourlyRateCents(displayRateCents));
      }
      return;
    }
    if (reason === 'blur' || reason === 'enter') {
      setRateDraft((cents / 100).toFixed(2));
    }
    if (
      cents === lastSubmittedRateCentsRef.current
      && lastFailedRateCentsRef.current !== cents
    ) {
      laborRateQueuedCentsRef.current = null;
      return;
    }
    if (cents === displayRateCents && lastSubmittedRateCentsRef.current == null) {
      dirtyRateRef.current = false;
      setRateUpdating(false);
      laborRateQueuedCentsRef.current = null;
      return;
    }
    laborRateQueuedCentsRef.current = cents;
    void flushHourlyRateSaveRef.current();
  };

  useEffect(() => {
    if (!editable) return;
    const cents = parseHourlyRateDraft(rateDraft);
    if (cents == null) return;
    if (cents === lastSubmittedRateCentsRef.current) return;
    if (cents === displayRateCents && lastSubmittedRateCentsRef.current == null) {
      dirtyRateRef.current = false;
      setRateUpdating(false);
      return;
    }
    setRateUpdating(true);
    const timer = window.setTimeout(() => {
      persistHourlyRateRef.current('debounce');
    }, LABOR_RATE_SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [rateDraft, editable, displayRateCents]);

  const hourlyRateField = !editable ? (
    <Box data-testid="billing-hourly-rate-section">
      <Typography variant="subtitle2" gutterBottom>
        {ti('workOrderBilling.hourlyRateSection')}
      </Typography>
      <Typography variant="body2">
        {displayRateCents > 0 ? formatPrice(displayRateCents) : '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('detail.hourlyRateHint')}
      </Typography>
    </Box>
  ) : (
    <Box data-testid="billing-hourly-rate-section">
      <Typography variant="subtitle2" gutterBottom>
        {ti('workOrderBilling.hourlyRateSection')}
      </Typography>
      <TextField
        size="small"
        label={t('detail.hourlyRate')}
        value={rateDraft}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9.,]/g, '');
          setRateDraft(next);
          dirtyRateRef.current = true;
          const parsed = parseHourlyRateDraft(next);
          setRateUpdating(parsed == null || parsed !== displayRateCents);
        }}
        onBlur={() => persistHourlyRateRef.current('blur')}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          persistHourlyRateRef.current('enter');
        }}
        fullWidth
        {...(rateUpdating
          ? {
              helperText: (
                <span data-testid="billing-hourly-rate-updating">
                  {t('detail.laborRateUpdating')}
                </span>
              ),
            }
          : {})}
        slotProps={{
          htmlInput: {
            'data-testid': 'billing-hourly-rate',
            'aria-busy': rateUpdating,
          },
          input: {
            endAdornment: <InputAdornment position="end">{t('detail.hourlyRateHint')}</InputAdornment>,
          },
        }}
      />
    </Box>
  );

  return (
    <>
      <PaymentLedgerProvider value={paymentLedgerContext}>
      <Card variant={cardVariant} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography variant="h6">
              {t('detail.payments')}
            </Typography>
            {(canReadInvoices || !billingLocked || overflowActions.length > 0) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canReadInvoices && (
                  // Always available (#2095): before an invoice exists this renders
                  // the would-be invoice straight from the work order; afterwards it
                  // opens the draft or finalized invoice itself.
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    data-testid="billing-preview-invoice"
                    onClick={() => {
                      const open = workOrderInvoice
                        ? openInvoicePdf(workOrderInvoice.id)
                        : openInvoicePreviewPdf(workOrder.id, workOrder.vatPercent ?? DEFAULT_VAT_PERCENT);
                      void open.catch((err: unknown) => {
                        notify.error(ti('actions.previewInvoiceFailed'), translateBillingError(err, ti));
                      });
                    }}
                  >
                    {ti('actions.previewInvoice')}
                  </Button>
                )}
                {!billingLocked && (
                  <Button
                    size="small"
                    variant={editMode ? 'contained' : 'outlined'}
                    startIcon={editMode ? <DoneIcon /> : <EditIcon />}
                    data-testid="billing-edit-toggle"
                    onClick={() => setEditMode((current) => !current)}
                  >
                    {editMode ? ti('workOrderBilling.done') : tc('edit')}
                  </Button>
                )}
                {overflowActions.length > 0 && (
                  <>
                    <Tooltip title={tc('moreOptions')}>
                      <IconButton
                        size="small"
                        aria-label={tc('moreOptions')}
                        data-testid="billing-overflow-button"
                        onClick={(event) => setOverflowAnchor(event.currentTarget)}
                        sx={{ minWidth: 36, minHeight: 36 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={overflowAnchor}
                      open={Boolean(overflowAnchor)}
                      onClose={() => setOverflowAnchor(null)}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    >
                      {overflowActions.map((action) => (
                        <MenuItem
                          key={action.key}
                          data-testid={`billing-overflow-${action.key}`}
                          {...(action.to
                            ? { component: RouterLink, to: action.to }
                            : {})}
                          onClick={() => {
                            setOverflowAnchor(null);
                            action.onClick?.();
                          }}
                        >
                          <ListItemIcon>{action.icon}</ListItemIcon>
                          {action.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Box>
            )}
          </Box>
          <BillingLineActionsContext.Provider
            value={{
              resetFixedServicePrice: handleResetFixedServicePrice,
              openServiceLabor: onOpenServiceLabor,
            }}
          >
            <GroupedInvoiceLines
              lines={annotatedLines}
              formatPrice={formatPrice}
              fallbackTaxPercent={fallbackTaxPercent}
              emptyLabel={preview.isPending || invoiceDetail.isPending
                ? t('detail.billingLinesLoading')
                : t('detail.noBillingLines')}
              editable={editable}
              saving={billing.isPending}
              onCommitQuantity={handleCommitQuantity}
              onCommitUnitPrice={handleCommitUnitPrice}
              onCommitDiscount={handleCommitDiscount}
              onDeleteLine={(line) => setLinePendingDelete(line)}
              onAddLine={handleAddLine}
            />
          </BillingLineActionsContext.Provider>
          <BillActualServices
            services={workOrder.services}
            canEditBillableHours={canEditBillableHours}
            billingLocked={hoursBillingLocked}
            pendingServiceId={billActual.isPending ? billActualServiceId : null}
            onRequestBillActual={setBillActualServiceId}
          />
          <Divider sx={{ my: 1.5 }} />
          {billingTotals && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('detail.billingSubtotal')}
                </Typography>
                <Typography variant="body2">{formatPrice(billingTotals.subtotalCents)}</Typography>
              </Box>
              {(billingTotals.taxBreakdown ?? []).filter((group) => group.taxPercent > 0).length > 0
                ? (billingTotals.taxBreakdown ?? [])
                  .filter((group) => group.taxPercent > 0)
                  .map((group) => (
                    <Box key={group.taxPercent} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${t('detail.billingTax')} ${String(group.taxPercent).replace('.', ',')} %`}
                      </Typography>
                      <Typography variant="body2">{formatPrice(group.taxCents)}</Typography>
                    </Box>
                  ))
                : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('detail.billingTax')}
                    </Typography>
                    <Typography variant="body2">{formatPrice(billingTotals.taxCents)}</Typography>
                  </Box>
                )}
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('total')}
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {displayTotalCents != null ? formatPrice(displayTotalCents) : '—'}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('detail.vatIncluded')}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <WorkOrderSurchargeRow workOrder={workOrder} locked={billingLocked} />
          <Divider sx={{ my: 2 }} />
          {hourlyRateField}
          {workOrder.isDisputed && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="billing-dispute-reason">
              {t('workOrderActions.disputeReason')}: {workOrder.disputeReason || '—'}
            </Alert>
          )}
          {workOrder.isInvoicingHold && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="billing-invoicing-hold-reason">
              {t('workOrderActions.invoicingHoldReason')}: {workOrder.invoicingHoldReason || '—'}
            </Alert>
          )}
          {voidErrorOfferCredit && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              data-testid="billing-void-credit-hint"
              action={invoiceDetail.data ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setCreditOpen(true)}
                >
                  {ti('actions.creditNote')}
                </Button>
              ) : undefined}
            >
              {voidErrorOfferCredit}
            </Alert>
          )}
          {primaryAction && (
            <Button
              variant="contained"
              color="primary"
              data-testid="billing-primary-action"
              startIcon={primaryAction.kind === 'pay'
                ? <PaymentIcon />
                : <ReceiptLongIcon />}
              onClick={handlePrimaryAction}
              disabled={primaryAction.disabled}
              sx={{ mt: 2 }}
            >
              {primaryAction.label}
            </Button>
          )}
          {primaryAction?.kind === 'pay' && canSendAsInvoice && (
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              data-testid="billing-send-as-invoice"
              onClick={handleSendAsInvoice}
              disabled={deliverInvoice.isPending}
              sx={{ mt: 2, ml: 1 }}
            >
              {t('serviceActions.sendAsInvoice')}
            </Button>
          )}
          {primaryAction?.kind === 'pay' && hasReversedPayment && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t('serviceActions.paymentReversedHint')}
            </Typography>
          )}
          {!workOrderInvoice && historyDetail.data?.payments && historyDetail.data.payments.length > 0 && (
            <PaymentHistorySection
              payments={historyDetail.data.payments}
              remainingCents={0}
            />
          )}
        </CardContent>
      </Card>
      {workOrderInvoice && (
        <InvoiceSummaryCard
          invoice={workOrderInvoice}
          cardVariant={cardVariant}
        />
      )}
      {/* Link status, re-send and copy for the work order's invoice (#2978).
          Same section as the invoice page; it hides itself unless the invoice
          is open and Svea is enabled. */}
      {invoiceDetail.data && (
        <SveaPaymentSection invoice={invoiceDetail.data} canManage={canManageInvoices} />
      )}

      {invoicesHistory.data && invoicesHistory.data.length > 0 && (
        <PreviousInvoicesSection invoices={invoicesHistory.data} />
      )}
      </PaymentLedgerProvider>

      <ConfirmDialog
        open={linePendingDelete != null}
        title={ti('workOrderBilling.deleteLineTitle')}
        message={ti('workOrderBilling.deleteLineMessage')}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setLinePendingDelete(null)}
        loading={billing.isPending}
      />

      <ConfirmDialog
        open={billActualServiceId != null}
        title={t('detail.billActualConfirmTitle')}
        message={t('detail.billActualConfirmMessage', { hours: billActualHoursLabel })}
        confirmText={t('detail.billActual', { hours: billActualHoursLabel })}
        onConfirm={() => { void handleConfirmBillActual(); }}
        onCancel={() => {
          if (!billActual.isPending) setBillActualServiceId(null);
        }}
        loading={billActual.isPending}
      />

      <PreInvoiceChecklistDialog
        open={checklistOpen}
        onClose={() => setChecklistOpen(false)}
        onConfirm={handleChecklistConfirm}
        workOrderId={workOrder.id}
        finalInspectionCompleted={workOrder.finalInspectionCompleted === true}
        laborCount={laborCount}
        partsCount={partsCount}
      />

      <GenerateInvoiceDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onCreated={setPostInvoiceDocuments}
        workOrderId={workOrder.id}
        customerEmail={customerEmail}
        taxPercent={fallbackTaxPercent}
        {...(displayTotalCents != null ? { totalCents: displayTotalCents } : {})}
        {...(workOrder.vehicle?.registrationNumber
          ? { vehicleRegistration: workOrder.vehicle.registrationNumber }
          : {})}
      />

      <PostInvoiceDocumentsDialog
        open={postInvoiceDocuments !== null}
        onClose={() => setPostInvoiceDocuments(null)}
        workOrder={workOrder}
        {...(postInvoiceDocuments
          ? {
              invoiceId: postInvoiceDocuments.invoiceId,
              ...(postInvoiceDocuments.receiptId
                ? { receiptId: postInvoiceDocuments.receiptId }
                : {}),
            }
          : {})}
      />

      <RecordPaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        workOrderId={workOrder.id}
        totalCents={workOrderInvoice?.totalCents ?? displayTotalCents ?? 0}
        paidAmountCents={workOrderInvoice?.paidAmountCents ?? 0}
        customerEmail={customerEmail}
        {...(workOrderInvoice
          ? { invoiceId: workOrderInvoice.id, invoiceNumber: workOrderInvoice.invoiceNumber }
          : {})}
      />

      <ConfirmDialog
        open={voidOpen}
        title={ti('actions.voidTitle')}
        message={ti('actions.voidConfirm', { invoiceNumber: workOrderInvoice?.invoiceNumber ?? '' })}
        confirmText={ti('actions.void')}
        confirmColor="error"
        onConfirm={() => void handleVoid()}
        onCancel={() => setVoidOpen(false)}
        loading={voidInvoice.isPending}
      />

      {creditOpen && invoiceDetail.data && (
        <CreditNoteDialog
          open={creditOpen}
          onClose={() => setCreditOpen(false)}
          invoice={invoiceDetail.data}
        />
      )}
    </>
  );
}
